// ApiServiceTeacher.js - Service API pour le tableau de bord enseignant
import { ENV_CONFIG } from '../config/environment';
import { ApiServiceShared } from './ApiServiceShared';
import { dateStrInTz, formatTimeInTz, getAppTimezone } from '../utils/dateTime';

// Configuration pour activer/désactiver les logs de debug
const DEBUG_LOGS = false;
// eslint-disable-next-line no-unused-vars
const debugLog = (...args) => {
  if (DEBUG_LOGS) console.log(...args);
};

class ApiServiceTeacher {
  constructor() {
    this.baseUrl = ENV_CONFIG.apiBaseUrl;
    this.token = null;
    this.authErrorHandler = null; // Handler pour les erreurs d'authentification
  }

  static getInstance() {
    if (!ApiServiceTeacher.instance) {
      ApiServiceTeacher.instance = new ApiServiceTeacher();
    }
    return ApiServiceTeacher.instance;
  }

  // Méthode pour définir le handler d'erreur d'authentification
  setAuthErrorHandler(handler) {
    this.authErrorHandler = handler;
  }

  // Méthode pour s'assurer que le token est présent
  _ensureToken() {
    if (!this.token) {
      this.token = localStorage.getItem('app_token');
    }
    
    return this.token;
  }

  // Méthode pour gérer les erreurs d'authentification et rediriger vers la page d'accueil
  _handleAuthError(response, errorMessage) {
    // Vérifier si c'est une erreur 401 (Token d'accès requis)
    if (response && response.status === 401) {
      console.error('❌ Erreur d\'authentification (401)');
      
      // Nettoyer le localStorage
      localStorage.removeItem('app_token');
      localStorage.removeItem('app_user');
      localStorage.removeItem('login_timestamp');
      
      // Utiliser le handler personnalisé si disponible, sinon fallback sur reload
      if (this.authErrorHandler && typeof this.authErrorHandler === 'function') {
        console.log('✅ TEACHER API - Utilisation du handler de navigation personnalisé');
        this.authErrorHandler();
      } else {
        console.log('⚠️ TEACHER API - Handler non disponible, utilisation de window.location.reload()');
        window.location.reload();
      }
      
      return true; // Indique qu'une redirection a été effectuée
    }
    return false; // Pas d'erreur d'authentification
  }

  // Méthode pour décoder le token JWT et extraire l'ID de l'utilisateur
  _getUserIdFromToken() {
    try {
      const token = this._ensureToken();
      if (!token) return null;
      
      // Décoder le token JWT (format: header.payload.signature)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const decoded = JSON.parse(jsonPayload);
      return decoded.id || null;
    } catch (error) {
      console.error('❌ Erreur lors du décodage du token:', error);
      return null;
    }
  }

  // Méthode utilitaire pour décoder le token et extraire les infos
  _decodeToken() {
    let teacherIdFromToken = null;
    let decodedTokenInfo = null;
    
    if (this.token) {
      try {
        const parts = this.token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          teacherIdFromToken = payload.id || payload.user_id || payload.userId;
          decodedTokenInfo = {
            id: teacherIdFromToken,
            username: payload.username,
            type: payload.type
          };
        }
      } catch (decodeError) {
        // Ignorer l'erreur de décodage
      }
    }
    
    return { teacherIdFromToken, decodedTokenInfo };
  }

  // Récupérer l'utilisateur actuel depuis localStorage
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('app_user');
      if (userStr) {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      return null;
    }
  }

  // ==================== TEACHER PROFILE ====================
  
  // Récupérer le profil de l'enseignant
  async getTeacherProfile() {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/teacher/profile`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 TEACHER API - getTeacherProfile - URL complète:', url);
      console.log('🔍 TEACHER API - getTeacherProfile - Méthode: GET');
      console.log('🔍 TEACHER API - getTeacherProfile - Headers:', headers);
      console.log('🔍 TEACHER API - getTeacherProfile - Token présent:', !!this.token);
      console.log('🔍 TEACHER API - getTeacherProfile - Paramètres: aucun (GET request)');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      console.log('🔍 TEACHER API - getTeacherProfile - Status:', response.status);
      console.log('🔍 TEACHER API - getTeacherProfile - Response Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 TEACHER API - getTeacherProfile - Response complète:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        console.log('❌ TEACHER API - getTeacherProfile - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ TEACHER API - getTeacherProfile - Succès');
      console.log('✅ TEACHER API - getTeacherProfile - Profil récupéré:', data.data?.full_name || data.data?.fullName || 'N/A');

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - getTeacherProfile - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du profil enseignant'
      };
    }
  }

  // ==================== TEACHER STUDENTS ====================
  
  // Récupérer les étudiants assignés à l'enseignant
  async getTeacherStudents(forceRefresh = false) {
    try {
      this._ensureToken();
      
      // Ajouter un paramètre de cache-busting si on force le refresh
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${this.baseUrl}/api/teacher/students${cacheBuster}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        // Forcer le rechargement en désactivant le cache du navigateur
        ...(forceRefresh && { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' })
      };
      
      console.log('🔍 TEACHER API - getTeacherStudents - URL complète:', url);
      console.log('🔍 TEACHER API - getTeacherStudents - Méthode: GET');
      console.log('🔍 TEACHER API - getTeacherStudents - Headers:', headers);
      console.log('🔍 TEACHER API - getTeacherStudents - Token présent:', !!this.token);
      console.log('🔍 TEACHER API - getTeacherStudents - Paramètres: aucun (GET request)');
      console.log('🔍 TEACHER API - getTeacherStudents - Force refresh:', forceRefresh);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        // Forcer le rechargement en désactivant le cache du navigateur
        cache: forceRefresh ? 'no-store' : 'default'
      });

      const data = await response.json();

      //console.log('🔍 TEACHER API - getTeacherStudents - Status:', response.status);
      //console.log('🔍 TEACHER API - getTeacherStudents - Response complète:', JSON.stringify(data, null, 2));
      //console.log('🔍 TEACHER API - getTeacherStudents - Nombre d\'étudiants:', data.data?.length || 0);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - getTeacherStudents - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants'
      };
    }
  }

  // ==================== QURAN RECORDS ====================
  
  // Récupérer les enregistrements Coran de l'enseignant
  async getTeacherQuranRecords(forceRefresh = false) {
    try {
      this._ensureToken();
      
      // Ajouter un paramètre de cache-busting si on force le refresh
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${this.baseUrl}/api/teacher/quran-records${cacheBuster}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        // Forcer le rechargement en désactivant le cache du navigateur
        ...(forceRefresh && { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' })
      };
      
      console.log('🔍 TEACHER API - getTeacherQuranRecords - URL complète:', url);
      console.log('🔍 TEACHER API - getTeacherQuranRecords - Méthode: GET');
      console.log('🔍 TEACHER API - getTeacherQuranRecords - Headers:', headers);
      console.log('🔍 TEACHER API - getTeacherQuranRecords - Token présent:', !!this.token);
      console.log('🔍 TEACHER API - getTeacherQuranRecords - Paramètres: aucun (GET request)');
      console.log('🔍 TEACHER API - getTeacherQuranRecords - Force refresh:', forceRefresh);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        // Forcer le rechargement en désactivant le cache du navigateur
        cache: forceRefresh ? 'no-store' : 'default'
      });

      const data = await response.json();

      //console.log('🔍 TEACHER API - getTeacherQuranRecords - Status:', response.status);
      //console.log('🔍 TEACHER API - getTeacherQuranRecords - Response complète:', JSON.stringify(data, null, 2));
      //console.log('🔍 TEACHER API - getTeacherQuranRecords - Nombre d\'enregistrements:', data.data?.length || 0);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - getTeacherQuranRecords - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des سجلات القرآن'
      };
    }
  }

  // Ajouter un nouvel enregistrement Coran
  async addQuranRecord(recordData) {
    try {
      this._ensureToken();
      
      // Obtenir le teacher_id depuis le token JWT
      const teacher_id = this._getUserIdFromToken();
      if (!teacher_id) {
        throw new Error('Impossible de récupérer l\'ID de l\'enseignant depuis le token');
      }
      
      // Mapper les champs du frontend vers le backend
      // Pour un ajout, toujours utiliser la date/heure actuelle au format DATETIME
      // IGNORER complètement recordData.date si elle existe (ne jamais l'utiliser pour un ajout)
      const now = new Date();
      const modifiedDate = now.toISOString().slice(0, 19).replace('T', ' '); // Format: "YYYY-MM-DD HH:mm:ss"
      
      // Log pour déboguer la date
      console.log('📅 TEACHER API - addQuranRecord - Date système:', {
        now: now.toString(),
        isoString: now.toISOString(),
        modifiedDate: modifiedDate,
        recordDataDate: recordData.date || '(non fournie)',
        timezoneOffset: now.getTimezoneOffset()
      });
      
      // Mapper les données frontend (camelCase) vers DTO backend (camelCase)
      const backendData = {
        studentId: parseInt(recordData.studentId) || 0,
        teacherId: parseInt(teacher_id) || 0,
        totalHizb: parseFloat(recordData.totalHizb) || 0,
        startPoint: recordData.startPoint || 'الناس',
        note: recordData.note || null,
        modifiedDate: modifiedDate,
        
        // Évaluations (OPTIONNELLES - ne pas inclure si vides/null/undefined)
        // Le backend utilisera 'NotDone' par défaut si non fourni
        ...(recordData.evalNewHifz && typeof recordData.evalNewHifz === 'string' && recordData.evalNewHifz.trim() !== '' 
          ? { evalNewHifz: recordData.evalNewHifz.trim() } 
          : {}),
        ...(recordData.evalOldHifzMurajah && typeof recordData.evalOldHifzMurajah === 'string' && recordData.evalOldHifzMurajah.trim() !== '' 
          ? { evalOldHifzMurajah: recordData.evalOldHifzMurajah.trim() } 
          : {}),
        ...(recordData.evalNewHifzMurajah && typeof recordData.evalNewHifzMurajah === 'string' && recordData.evalNewHifzMurajah.trim() !== '' 
          ? { evalNewHifzMurajah: recordData.evalNewHifzMurajah.trim() } 
          : {}),
        
        // ✅ repeatNewHifz : valeur de "الجلسة القادمة" (quran_next_session) à sauvegarder dans "آخر جلسة" (quran_records)
        repeatNewHifz: (recordData.repeatNewHifz !== undefined && recordData.repeatNewHifz !== null) 
          ? parseInt(recordData.repeatNewHifz) 
          : 0,
        
        // Nouveau Hifz
        newHifzTxt: recordData.newHifzTxt || null,
        newHifzRange: recordData.newHifzRange || null,
        
        // Ancien Hifz - Murajaah
        oldHifzMurajahTxt: recordData.oldHifzMurajahTxt || null,
        oldHifzMurajahRange: recordData.oldHifzMurajahRange || null,
        
        // Nouveau Hifz - Murajaah (NOUVEAU)
        newHifzMurajahText: recordData.newHifzMurajahText || null,
        newHifzMurajahRange: recordData.newHifzMurajahRange || null,
        
        // ❌ SUPPRIMÉ : Les champs nextHalaqa* sont maintenant gérés dans quran_next_session
        // Utiliser createOrUpdateNextSession() séparément pour créer/mettre à jour la session suivante
      };
      
      const url = `${this.baseUrl}/api/teacher/quran-records`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs essentiels uniquement
      console.log('🔍 POST /api/teacher/quran-records');
      console.log('📤 Données envoyées:', backendData);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });

      const data = await response.json();

      console.log('📥 Données reçues:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        // Afficher plus de détails sur l'erreur
        let errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        
        // Gérer spécifiquement l'erreur de contrainte unique (date dupliquée)
        const errorString = JSON.stringify(data).toLowerCase();
        if (errorString.includes('duplicate entry') || 
            errorString.includes('unique_student_date') ||
            (data.error && (data.error.includes('Duplicate entry') || data.error.includes('unique_student_date'))) ||
            (data.sqlMessage && data.sqlMessage.includes('Duplicate entry'))) {
          errorMessage = 'يوجد بالفعل جلسة لهذا الطالب في هذا التاريخ. يرجى تعديل الجلسة الموجودة بدلاً من إنشاء جلسة جديدة.';
        }
        
        console.error('❌ TEACHER API - addQuranRecord - Erreur détaillée:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          data: data,
          errorString: errorString
        });
        
        const error = new Error(errorMessage);
        error.isDuplicateEntry = errorString.includes('duplicate entry') || errorString.includes('unique_student_date');
        throw error;
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - addQuranRecord - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout du سجل القرآن'
      };
    }
  }

  // Supprimer un enregistrement Coran
  async deleteQuranRecord(recordId) {
    try {
      this._ensureToken();
      
      console.log('🔍 TEACHER API - deleteQuranRecord - ID:', recordId);
      console.log('🔍 TEACHER API - deleteQuranRecord - Route:', `${this.baseUrl}/api/teacher/quran-records/${recordId}`);
      
      const response = await fetch(`${this.baseUrl}/api/teacher/quran-records/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      console.log('🔍 TEACHER API - deleteQuranRecord - Status:', response.status);
      console.log('🔍 TEACHER API - deleteQuranRecord - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression du سجل القرآن'
      };
    }
  }

  // Récupérer un enregistrement Coran par ID
  async getQuranRecordById(recordId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/teacher/quran-records/${recordId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 TEACHER API - getQuranRecordById - ID:', recordId);
      console.log('🔍 TEACHER API - getQuranRecordById - URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      console.log('🔍 TEACHER API - getQuranRecordById - Status:', response.status);
      console.log('🔍 TEACHER API - getQuranRecordById - Response data:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      // S'assurer que data.data existe, sinon utiliser data directement
      const recordData = data.data || data;
      console.log('🔍 TEACHER API - getQuranRecordById - Record data final:', JSON.stringify(recordData, null, 2));

      return {
        success: true,
        data: recordData
      };
    } catch (error) {
      console.error('❌ TEACHER API - getQuranRecordById - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du سجل القرآن'
      };
    }
  }

  // Mettre à jour un enregistrement Coran
  async updateQuranRecord(recordId, recordData) {
    try {
      this._ensureToken();
      
      // Obtenir le teacher_id depuis le token JWT
      const teacher_id = this._getUserIdFromToken();
      if (!teacher_id) {
        throw new Error('Impossible de récupérer l\'ID de l\'enseignant depuis le token');
      }
      
      // ✅ NOUVEAU : Construction dynamique - n'envoyer QUE les champs fournis dans recordData
      // Pour une mise à jour partielle, on n'envoie que les champs fournis
      const backendData = {};
      
      // ⚠️ studentId : Ne pas inclure - l'ID dans l'URL est suffisant pour identifier l'enregistrement
      // ⚠️ teacherId : Ne pas inclure - le backend vérifie l'authentification via le token JWT
      // ⚠️ repeatNewHifz : Ne pas inclure - utiliser la route dédiée PATCH /api/quran-records/:id/repeat-new-hifz
      // Ces champs ne doivent pas être envoyés dans le body pour une mise à jour partielle
      
      // Exclure explicitement repeatNewHifz si présent dans recordData
      const { repeatNewHifz, ...dataWithoutRepeatNewHifz } = recordData;
      
      if (dataWithoutRepeatNewHifz.totalHizb !== undefined) backendData.totalHizb = parseFloat(dataWithoutRepeatNewHifz.totalHizb) || 0;
      if (dataWithoutRepeatNewHifz.startPoint !== undefined) backendData.startPoint = dataWithoutRepeatNewHifz.startPoint || 'الناس';
      if (dataWithoutRepeatNewHifz.note !== undefined) backendData.note = dataWithoutRepeatNewHifz.note || null;
      if (dataWithoutRepeatNewHifz.modifiedDate !== undefined) backendData.modifiedDate = dataWithoutRepeatNewHifz.modifiedDate || new Date().toISOString().slice(0, 19).replace('T', ' ');
      
      // Évaluations (OPTIONNELLES - inclure UNIQUEMENT si fournies)
      if (dataWithoutRepeatNewHifz.evalNewHifz !== undefined && dataWithoutRepeatNewHifz.evalNewHifz !== null && dataWithoutRepeatNewHifz.evalNewHifz !== '') {
        backendData.evalNewHifz = typeof dataWithoutRepeatNewHifz.evalNewHifz === 'string' ? dataWithoutRepeatNewHifz.evalNewHifz.trim() : dataWithoutRepeatNewHifz.evalNewHifz;
      }
      if (dataWithoutRepeatNewHifz.evalOldHifzMurajah !== undefined && dataWithoutRepeatNewHifz.evalOldHifzMurajah !== null && dataWithoutRepeatNewHifz.evalOldHifzMurajah !== '') {
        backendData.evalOldHifzMurajah = typeof dataWithoutRepeatNewHifz.evalOldHifzMurajah === 'string' ? dataWithoutRepeatNewHifz.evalOldHifzMurajah.trim() : dataWithoutRepeatNewHifz.evalOldHifzMurajah;
      }
      if (dataWithoutRepeatNewHifz.evalNewHifzMurajah !== undefined && dataWithoutRepeatNewHifz.evalNewHifzMurajah !== null && dataWithoutRepeatNewHifz.evalNewHifzMurajah !== '') {
        backendData.evalNewHifzMurajah = typeof dataWithoutRepeatNewHifz.evalNewHifzMurajah === 'string' ? dataWithoutRepeatNewHifz.evalNewHifzMurajah.trim() : dataWithoutRepeatNewHifz.evalNewHifzMurajah;
      }
      
      // ❌ repeatNewHifz : NE PAS INCLURE (utiliser la route dédiée PATCH /api/quran-records/:id/repeat-new-hifz)
      // Ce champ est EXCLUS explicitement pour éviter l'erreur du backend
      
      // Nouveau Hifz
      if (dataWithoutRepeatNewHifz.newHifzTxt !== undefined) backendData.newHifzTxt = dataWithoutRepeatNewHifz.newHifzTxt || null;
      if (dataWithoutRepeatNewHifz.newHifzRange !== undefined) backendData.newHifzRange = dataWithoutRepeatNewHifz.newHifzRange || null;
      
      // Ancien Hifz - Murajaah
      if (dataWithoutRepeatNewHifz.oldHifzMurajahTxt !== undefined) backendData.oldHifzMurajahTxt = dataWithoutRepeatNewHifz.oldHifzMurajahTxt || null;
      if (dataWithoutRepeatNewHifz.oldHifzMurajahRange !== undefined) backendData.oldHifzMurajahRange = dataWithoutRepeatNewHifz.oldHifzMurajahRange || null;
      
      // Nouveau Hifz - Murajaah (NOUVEAU)
      if (dataWithoutRepeatNewHifz.newHifzMurajahText !== undefined) backendData.newHifzMurajahText = dataWithoutRepeatNewHifz.newHifzMurajahText || null;
      if (dataWithoutRepeatNewHifz.newHifzMurajahRange !== undefined) backendData.newHifzMurajahRange = dataWithoutRepeatNewHifz.newHifzMurajahRange || null;
      
      // ❌ SUPPRIMÉ : Les champs nextHalaqa* sont maintenant gérés dans quran_next_session
      // Utiliser updateNextSessionByStudent() ou createOrUpdateNextSession() séparément pour créer/mettre à jour la session suivante
      
      // Logs essentiels uniquement
      console.log('🔍 PUT /api/quran-records/' + recordId);
      console.log('📤 Données envoyées:', backendData);
      
      const response = await fetch(`${this.baseUrl}/api/quran-records/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(backendData)
      });

      const data = await response.json();

      console.log('📥 Données reçues:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        // Afficher plus de détails sur l'erreur
        const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        console.error('❌ TEACHER API - updateQuranRecord - Erreur détaillée:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          data: data
        });
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du سجل القرآن'
      };
    }
  }

  // ==================== TEACHER MESSAGES ====================
  
  // Récupérer les messages de l'enseignant
  async getTeacherMessages() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/teacher/messages`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: data?.message || `HTTP error! status: ${response.status}`,
          response_body: data
        };
        console.error('❌ TEACHER API - getTeacherMessages - Erreur HTTP:', JSON.stringify(errorData, null, 2));
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      // NOUVEAU SCHÉMA: Parser recipient_ids si c'est une string JSON
      // Utiliser created_date au lieu de sent_date/sent_time
      // student_id, student_gender et student_label sont maintenant inclus dans la réponse
      const messages = (data?.data || data || []).map(msg => ({
        ...msg,
        // Parser recipient_ids si c'est une string
        recipient_ids: typeof msg.recipient_ids === 'string' 
          ? (msg.recipient_ids ? JSON.parse(msg.recipient_ids) : [])
          : (msg.recipient_ids || []),
        // Utiliser created_date pour la date/heure (sent_date et sent_time n'existent plus)
        // ✅ rendues dans le fuseau de l'ASSOCIATION (`default_timezone`), pas dans celui du poste
        sent_date: msg.created_date ? dateStrInTz(new Date(msg.created_date), getAppTimezone()) : null,
        sent_time: msg.created_date ? formatTimeInTz(new Date(msg.created_date), getAppTimezone(), 'fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null,
        // student_id est maintenant inclus dans la réponse (peut être null)
        student_id: msg.student_id || null,
        // student_gender et student_label sont maintenant inclus dans la réponse (peuvent être null)
        student_gender: msg.student_gender || null,
        student_label: msg.student_label || null,
        // is_read n'existe plus dans le nouveau schéma
        is_read: false
      }));

      return {
        success: true,
        data: messages
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ TEACHER API - getTeacherMessages - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des الرسائل'
      };
    }
  }

  // Marquer un message comme lu
  async markMessageAsRead(messageId) {
    try {
      this._ensureToken();
      
      console.log('🔍 TEACHER API - markMessageAsRead - ID:', messageId);
      console.log('🔍 TEACHER API - markMessageAsRead - Route:', `${this.baseUrl}/api/teacher/messages/${messageId}/read`);
      
      const response = await fetch(`${this.baseUrl}/api/teacher/messages/${messageId}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      console.log('🔍 TEACHER API - markMessageAsRead - Status:', response.status);
      console.log('🔍 TEACHER API - markMessageAsRead - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du message'
      };
    }
  }

  // ==================== QUICK REPORTS ====================
  
  // Récupérer les parents d'un étudiant
  async getStudentParents(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/parent-students/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 TEACHER API - getStudentParents - URL:', url);
      console.log('🔍 TEACHER API - getStudentParents - studentId:', studentId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();
      
      console.log('🔍 TEACHER API - getStudentParents - Status:', response.status);
      console.log('🔍 TEACHER API - getStudentParents - Response:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        console.error('❌ TEACHER API - getStudentParents - Erreur HTTP:', response.status, data);
        // En cas d'erreur, retourner un tableau vide
        return {
          success: true,
          data: []
        };
      }

      return {
        success: true,
        data: data.data || data || []
      };
    } catch (error) {
      console.error('❌ TEACHER API - getStudentParents - Erreur:', error);
      // En cas d'erreur, retourner un tableau vide pour ne pas bloquer l'interface
      return {
        success: true,
        data: []
      };
    }
  }
  
  // Envoyer un rapport rapide
  async sendQuickReport(reportData) {
    try {
      this._ensureToken();
      
      const { teacherIdFromToken, decodedTokenInfo } = this._decodeToken();
      const currentUser = this.getCurrentUser();
      const teacherIdFromStorage = currentUser?.id || currentUser?.user_id || currentUser?.userId;
      
      // NOUVEAU SCHÉMA: Gérer recipient_type='admin' ou 'specific'
      const recipientType = reportData.recipient_type || 'specific';
      
      // Préparer les données de base
      const backendData = {
        student_id: reportData.studentId || 0, // Peut être 0 si on envoie uniquement à la direction
        recipient_type: recipientType, // 'admin' ou 'specific'
        report_type: reportData.reportType,      // OBLIGATOIRE
        content: reportData.message || reportData.content,  // OBLIGATOIRE
        priority: reportData.priority || 'normal', // OPTIONNEL (défaut: "normal")
        subject: reportData.subject || '', // Subject de la réponse
        is_reply: reportData.is_reply || false, // Indique si c'est une réponse
        reply_to_message_id: reportData.reply_to_message_id || null // ID du message auquel on répond
      };
      
      // Ajouter recipient_ids seulement s'il est fourni et non vide
      // Pour 'admin' avec recipient_ids vide, le backend enverra uniquement aux admins
      // Pour 'admin' avec recipient_ids non vide, le backend enverra aux admins + parents sélectionnés
      // Pour 'specific', recipient_ids est obligatoire (doit être fourni par le frontend)
      if (reportData.recipient_ids && Array.isArray(reportData.recipient_ids) && reportData.recipient_ids.length > 0) {
        backendData.recipient_ids = reportData.recipient_ids.map(id => parseInt(id)).filter(id => id > 0);
      }
      
      const url = `${this.baseUrl}/api/teacher/quick-report`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'POST',
        route: '/api/teacher/quick-report',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        teacher_id_from_token: teacherIdFromToken,
        teacher_id_from_storage: teacherIdFromStorage,
        decoded_token: decodedTokenInfo,
        base_url: this.baseUrl,
        parameters_frontend: {
          studentId: reportData.studentId,
          reportType: reportData.reportType,
          message: reportData.message?.substring(0, 100) + (reportData.message?.length > 100 ? '...' : ''),
          message_length: reportData.message?.length || 0,
          priority: reportData.priority,
          subject: reportData.subject || '',
          is_reply: reportData.is_reply || false,
          reply_to_message_id: reportData.reply_to_message_id || null
        },
        parameters_backend: {
          student_id: backendData.student_id,
          report_type: backendData.report_type,
          content: backendData.content?.substring(0, 100) + (backendData.content?.length > 100 ? '...' : ''),
          content_length: backendData.content?.length || 0,
          priority: backendData.priority,
          subject: backendData.subject || '',
          is_reply: backendData.is_reply || false,
          reply_to_message_id: backendData.reply_to_message_id || null
        },
        note: 'Le backend extrait automatiquement teacherId depuis req.user.id du token JWT'
      };
      console.log('📤 TEACHER API - sendQuickReport - Requête:', JSON.stringify(requestParams, null, 2));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });

      const data = await response.json();
      
      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        status_text: response.statusText,
        response_ok: response.ok,
        response_body: data,
        message_id: data?.data?.id || null,
        recipients_count: data?.data?.recipients_count || null,
        success: data?.success || false
      };
      console.log('📤 TEACHER API - sendQuickReport - Réponse:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: data?.message || `HTTP error! status: ${response.status}`,
          response_body: data
        };
        console.error('❌ TEACHER API - sendQuickReport - Erreur HTTP:', JSON.stringify(errorData, null, 2));
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ TEACHER API - sendQuickReport - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'envoi du التقرير السريع'
      };
    }
  }

  // ==================== ATTENDANCE ====================
  
  // Récupérer les statistiques de présence/absence
  async getAttendance(filters = {}) {
    try {
      this._ensureToken();
      const teacher_id = this._getUserIdFromToken();
      if (!teacher_id) {
        console.error('❌ TEACHER API - getAttendance - Impossible de récupérer teacher_id depuis le token');
        throw new Error('Impossible de récupérer l\'ID de l\'enseignant depuis le token');
      }

      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();
      
      // Toujours inclure le teacherId depuis le token
      queryParams.append('teacherId', teacher_id.toString());
      
      // Ajouter les filtres optionnels
      if (filters.studentId) {
        queryParams.append('studentId', filters.studentId.toString());
      }
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }
      // Année scolaire (restreint le périmètre des élèves via student_enrollments)
      if (filters.school_year_id != null && filters.school_year_id !== '') {
        queryParams.append('school_year_id', String(filters.school_year_id));
      }

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/quran-records/attendance${queryString ? `?${queryString}` : ''}`;

      console.log('🔍 TEACHER API - getAttendance - Début de l\'appel');
      console.log('🔍 TEACHER API - getAttendance - teacher_id depuis token:', teacher_id);
      console.log('🔍 TEACHER API - getAttendance - Filtres reçus:', JSON.stringify(filters, null, 2));
      console.log('🔍 TEACHER API - getAttendance - URL complète:', url);
      console.log('🔍 TEACHER API - getAttendance - Query params:', queryString);
      console.log('🔍 TEACHER API - getAttendance - Méthode: GET');
      console.log('🔍 TEACHER API - getAttendance - Token présent:', !!this.token);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 TEACHER API - getAttendance - Status HTTP:', response.status);
      console.log('🔍 TEACHER API - getAttendance - Response OK:', response.ok);

      const data = await response.json();
      console.log('🔍 TEACHER API - getAttendance - Réponse brute:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        console.error('❌ TEACHER API - getAttendance - Erreur HTTP:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          data: data
        });
        throw new Error(errorMessage);
      }

      // Vérifier la structure de la réponse
      const responseData = data.data || data;
      console.log('✅ TEACHER API - getAttendance - Données extraites:', JSON.stringify(responseData, null, 2));
      console.log('✅ TEACHER API - getAttendance - present:', responseData?.present);
      console.log('✅ TEACHER API - getAttendance - absent:', responseData?.absent);
      console.log('✅ TEACHER API - getAttendance - total:', responseData?.total);
      console.log('✅ TEACHER API - getAttendance - records count:', responseData?.records?.length || 0);

      return {
        success: true,
        data: responseData
      };
    } catch (error) {
      console.error('❌ TEACHER API - getAttendance - Erreur exception:', {
        message: error instanceof Error ? error.message : 'Erreur inconnue',
        error: error,
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des présents/absents'
      };
    }
  }

  // ==================== TEACHER STATISTICS ====================
  
  // Récupérer les statistiques de l'enseignant
  async getTeacherStats() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/teacher/stats`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 TEACHER API - getTeacherStats - URL complète:', url);
      console.log('🔍 TEACHER API - getTeacherStats - Méthode: GET');
      console.log('🔍 TEACHER API - getTeacherStats - Headers:', headers);
      console.log('🔍 TEACHER API - getTeacherStats - Token présent:', !!this.token);
      console.log('🔍 TEACHER API - getTeacherStats - Paramètres: aucun (GET request)');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      console.log('🔍 TEACHER API - getTeacherStats - Status:', response.status);
      console.log('🔍 TEACHER API - getTeacherStats - Response complète:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - getTeacherStats - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des الإحصائيات'
      };
    }
  }

  // ==================== AUTHENTICATION ====================
  
  // Connexion enseignant
  async login(username, password) {
    try {
      console.log('🔍 TEACHER API - login - Début');
      console.log('🔍 TEACHER API - login - Username:', username);
      console.log('🔍 TEACHER API - login - Route:', `${this.baseUrl}/api/auth/login`);
      
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password,
          user_type: 'teacher'
        })
      });

      const data = await response.json();

      console.log('🔍 TEACHER API - login - Status:', response.status);
      console.log('🔍 TEACHER API - login - Response:', data);

      if (!response.ok) {
        // Pour le login, ne PAS appeler _handleAuthError (pas de redirection)
        // Retourner simplement l'erreur pour que l'utilisateur puisse réessayer
        return {
          success: false,
          message: data.message || `Erreur de connexion (status: ${response.status})`
        };
      }

      // Sauvegarder le token et les données utilisateur avec timestamp pour expiration de 1 heure
      if (data.token) {
        this.setToken(data.token);
        localStorage.setItem('app_token', data.token);
        localStorage.setItem('app_user', JSON.stringify(data.user));
        localStorage.setItem('login_timestamp', Date.now().toString());
        console.log('✅ TEACHER API - login - Connexion réussie');
        console.log('✅ TEACHER API - login - Token stocké:', data.token ? 'Oui' : 'Non');
        console.log('✅ TEACHER API - login - User stocké:', data.user ? 'Oui' : 'Non');
        console.log('✅ TEACHER API - login - Message:', data.message);
      }

      return {
        success: true,
        data: data.user,
        token: data.token
      };
    } catch (error) {
      console.error('❌ TEACHER API - login - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la connexion'
      };
    }
  }

  // ==================== PASSWORD CHANGE ====================
  
  // Changer le mot de passe de l'enseignant
  async changePassword(passwordData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/teacher/change-password`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 TEACHER API - changePassword - URL complète:', url);
      console.log('🔍 TEACHER API - changePassword - Méthode: PUT');
      console.log('🔍 TEACHER API - changePassword - Headers:', headers);
      console.log('🔍 TEACHER API - changePassword - Token présent:', !!this.token);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          current_password: passwordData.oldPassword,
          new_password: passwordData.newPassword
        })
      });

      const data = await response.json();
      
      console.log('🔍 TEACHER API - changePassword - Status:', response.status);
      console.log('🔍 TEACHER API - changePassword - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ TEACHER API - changePassword - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe'
      };
    }
  }

  // ==================== UTILITY METHODS ====================
  
  // Test de connexion
  async testConnection() {
    try {
      console.log('🔍 TEACHER API - testConnection - Début');
      console.log('🔍 TEACHER API - testConnection - baseUrl:', this.baseUrl);
      
      const response = await fetch(`${this.baseUrl}/api/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 TEACHER API - testConnection - Status:', response.status);
      console.log('🔍 TEACHER API - testConnection - Response OK:', response.ok);

      const data = await response.json();
      console.log('🔍 TEACHER API - testConnection - Response JSON:', data);

      return {
        success: response.ok,
        status: response.status,
        data: data
      };
    } catch (error) {
      console.error('❌ TEACHER API - testConnection - Erreur:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Définir le token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('app_token', token);
    } else {
      localStorage.removeItem('app_token');
    }
  }

  // Obtenir le token
  getToken() {
    return this.token || localStorage.getItem('app_token');
  }

  // Déconnexion
  // ==================== QURAN NEXT SESSION ====================
  
  // Récupérer la session suivante d'un étudiant
  async getNextSessionForStudent(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || null,
        message: data.message
      };
    } catch (error) {
      console.error('❌ TEACHER API - getNextSessionForStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de la session suivante'
      };
    }
  }
  
  // Récupérer toutes les sessions suivantes d'un enseignant
  async getNextSessionsByTeacher(teacherId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/teacher/${teacherId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération des sessions suivantes'
        };
      }
      
      return {
        success: true,
        data: data.data || [],
        message: data.message
      };
    } catch (error) {
      console.error('❌ TEACHER API - getNextSessionsByTeacher - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des sessions suivantes'
      };
    }
  }
  
  // Créer ou mettre à jour la session suivante d'un étudiant (upsert)
  async createOrUpdateNextSession(studentId, sessionData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        studentId: parseInt(studentId),
        teacherId: sessionData.teacherId ? parseInt(sessionData.teacherId) : undefined,
        startPoint: sessionData.startPoint || 'الناس',
        nextHalaqaNewHifzText: sessionData.nextHalaqaNewHifzText || null,
        nextHalaqaNewHifzRange: sessionData.nextHalaqaNewHifzRange || null,
        nextHalaqaOldHifzMurajahText: sessionData.nextHalaqaOldHifzMurajahText || null,
        nextHalaqaOldHifzMurajahRange: sessionData.nextHalaqaOldHifzMurajahRange || null,
        nextHalaqaNewHifzMurajahText: sessionData.nextHalaqaNewHifzMurajahText || null,
        nextHalaqaNewHifzMurajahRange: sessionData.nextHalaqaNewHifzMurajahRange || null,
        repeatNewHifz: sessionData.repeatNewHifz !== undefined ? parseInt(sessionData.repeatNewHifz) : 0,
        modifiedDate: sessionData.modifiedDate || new Date().toISOString()
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la création/mise à jour de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante créée/mise à jour avec succès'
      };
    } catch (error) {
      console.error('❌ TEACHER API - createOrUpdateNextSession - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création/mise à jour de la session suivante'
      };
    }
  }
  
  // Mettre à jour la session suivante d'un étudiant (PUT)
  async updateNextSessionByStudent(studentId, sessionData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        teacherId: sessionData.teacherId ? parseInt(sessionData.teacherId) : undefined,
        startPoint: sessionData.startPoint || 'الناس',
        nextHalaqaNewHifzText: sessionData.nextHalaqaNewHifzText || null,
        nextHalaqaNewHifzRange: sessionData.nextHalaqaNewHifzRange || null,
        nextHalaqaOldHifzMurajahText: sessionData.nextHalaqaOldHifzMurajahText || null,
        nextHalaqaOldHifzMurajahRange: sessionData.nextHalaqaOldHifzMurajahRange || null,
        nextHalaqaNewHifzMurajahText: sessionData.nextHalaqaNewHifzMurajahText || null,
        nextHalaqaNewHifzMurajahRange: sessionData.nextHalaqaNewHifzMurajahRange || null,
        repeatNewHifz: sessionData.repeatNewHifz !== undefined ? parseInt(sessionData.repeatNewHifz) : 0,
        modifiedDate: sessionData.modifiedDate || new Date().toISOString()
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la mise à jour de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante mise à jour avec succès'
      };
    } catch (error) {
      console.error('❌ TEACHER API - updateNextSessionByStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la session suivante'
      };
    }
  }

  // Mettre à jour uniquement repeatNewHifz d'un enregistrement Coran
  async updateRepeatNewHifz(recordId, repeatNewHifz) {
    this._ensureToken();
    const handleAuthError = this._handleAuthError ? this._handleAuthError.bind(this) : null;
    return await ApiServiceShared.updateRepeatNewHifz(
      recordId, 
      repeatNewHifz, 
      this.token, 
      handleAuthError
    );
  }

  // ==================== GRADES MANAGEMENT ====================

  async getGrades(locale = 'ar') {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const response = await fetch(`${this.baseUrl}/api/admin/grades?lang=${encodeURIComponent(lang)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();
      
      console.log('🔍 TEACHER API - getGrades - Response:', {
        status: response.status,
        ok: response.ok,
        dataLength: data?.data?.length || 0,
        data: data
      });

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - getGrades - Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des notes'
      };
    }
  }

  async createGrade(gradeData, locale = 'ar') {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const response = await fetch(`${this.baseUrl}/api/admin/grades?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(gradeData)
      });

      const data = await response.json();
      
      console.log('🔍 TEACHER API - createGrade - Response:', {
        status: response.status,
        ok: response.ok,
        data: data
      });

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - createGrade - Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout de la note'
      };
    }
  }

  async deleteGrade(gradeId) {
    try {
      this._ensureToken();
      
      // Utiliser la route admin car il n'y a pas de route teacher spécifique
      const response = await fetch(`${this.baseUrl}/api/admin/grades/${gradeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();
      
      console.log('🔍 TEACHER API - deleteGrade - Response:', {
        status: response.status,
        ok: response.ok,
        data: data
      });

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ TEACHER API - deleteGrade - Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la note'
      };
    }
  }

  // ==================== SUBJECTS MANAGEMENT ====================

  async getSubjects(locale = 'ar') {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const response = await fetch(`${this.baseUrl}/api/admin/subjects?locale=${encodeURIComponent(lang)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des matières'
      };
    }
  }

  // ==================== PARAMETERS MANAGEMENT ====================

  async getParameters() {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}/api/admin/parameters`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des paramètres'
      };
    }
  }

  logout() {
    this.token = null;
    localStorage.removeItem('app_token');
    localStorage.removeItem('app_user');
    localStorage.removeItem('login_timestamp');
  }
}

export default ApiServiceTeacher;
